import HttpClient from "./HttpClient"
import { mapPhoto, mapCollection, mapEvent, mapYears } from "../Mappers"
import { locale } from "@albelli/photoselector"

interface Params {
  Skip?: number
  Limit?: number
  FromDate?: string
  ToDate?: string
  Favourite?: boolean
  SortingField?: string
  SortingDirection?: string
  Month?: number
  Year?: number
  SelectedMonth?: any
  SelectedYear?: number
}

class Api {
  private queryApiUrl = ""
  private token: () => Promise<string> = () => Promise.resolve("")
  private limit = 100

  constructor(queryApiUrl: string, token: () => Promise<string>) {
    this.queryApiUrl = queryApiUrl
    this.token = token
  }

  async status() {
    const status = await HttpClient.get(this.queryApiUrl + "/status/", this.token).catch(this.error("Failed to get status: "))

    return status
  }

  async getStats() {
    const stats = await HttpClient.get(this.queryApiUrl + `/photos/stats`, this.token).catch(this.error("Failed to get stats: "))

    return { stats }
  }

  async getLatestUploads(params: Params) {
    const { Skip } = params

    const latestUploads = await HttpClient.get(
      this.queryApiUrl + `/photos/?Skip=${Skip}&Limit=${this.limit}&SortingField=UploadDate&SortingDirection=Descending`,
      this.token
    ).catch(this.error("Failed to get latest uploads: "))

    return {
      totalItemsCount: latestUploads.MatchedPhotoCount,
      items: (latestUploads.Photos || []).map(mapPhoto),
      loading: false
    }
  }

  async getFavorites(params: Params) {
    const { Skip, SortingDirection } = params
    const favorites = await HttpClient.get(
      this.queryApiUrl + `/favourites/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}&SortingField=PhotoDate`,
      this.token
    ).catch(this.error("Failed to get all favorites: "))

    return {
      totalItemsCount: favorites.MatchedPhotoCount,
      items: (favorites.Photos || []).map(mapPhoto),
      loading: false
    }
  }

  async getCollections(params: Params) {
    const { Skip, SortingDirection, SortingField } = params

    const collections = await HttpClient.get(
      this.queryApiUrl +
        `/collections/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}&SortingField=${SortingField}&CollectionFilter=All`,
      this.token
    ).catch(this.error("Failed to get all collections: "))
    return {
      totalItemsCount: collections.MatchedCollectionCount,
      items: (collections.Collections || []).map(mapCollection),
      loading: false
    }
  }

  getCollection(collectionId: string) {
    return async (params: Params) => {
      const { Skip, SortingDirection } = params

      const collection = await HttpClient.get(
        this.queryApiUrl +
          `/collections/paged/${collectionId}/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}&SortingField=PhotoDate`,
        this.token
      ).catch(this.error("Failed to get collection: " + collectionId))

      return {
        title: collection.Name,
        totalItemsCount: collection.MatchedPhotoCount,
        items: (collection.Photos || []).map(mapPhoto),
        loading: false
      }
    }
  }

  async getEvents(params: Params) {
    const { Skip, SortingDirection, SelectedYear } = params

    const url =
      SelectedYear === 0
        ? this.queryApiUrl + `/events/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}`
        : this.queryApiUrl + `/events/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}&Year=${SelectedYear}`

    const events = await HttpClient.get(url, this.token).catch(this.error("Failed to get all events: "))

    const photoCountPerYear = await this._getPhotoCountPerYear().catch(this.error("Failed to get photocount per year: "))

    return {
      totalItemsCount: events.MatchedEventCount,
      items: (events.Events || []).map(mapEvent),
      loading: false,
      yearsWithPhotos: photoCountPerYear.map((year: any) => year.Year)
    }
  }

  getEvent(eventId: string) {
    return async (params: Params) => {
      const { Skip, SortingDirection } = params

      const event = await HttpClient.get(
        this.queryApiUrl + `/events/paged/${eventId}/?Skip=${Skip}&Limit=${this.limit}&SortingDirection=${SortingDirection}`,
        this.token
      ).catch(this.error("Failed to get event: " + eventId + " error: "))

      return {
        title: event.Name,
        totalItemsCount: event.MatchedPhotoCount,
        items: (event.Photos || []).map(mapPhoto),
        loading: false
      }
    }
  }

  getYear(yearId: string) {
    return async (params: Params) => {
      const { Skip, SortingDirection, SelectedMonth } = params

      if (yearId === locale.get().generic.nodate) {
        const photosWithoutDate = await this._getPhotosWithoutDate({ Skip, Limit: this.limit, SortingDirection }).catch(
          this.error("Failed to get photos without date: ")
        )

        return {
          title: locale.get().generic.nodate,
          totalItemsCount: photosWithoutDate.MatchedPhotoCount,
          items: (photosWithoutDate.Photos || []).map(mapPhoto),
          loading: false
        }
      } else {
        const timeline = await this._getTimeline({
          Year: Number(yearId),
          Month: SelectedMonth,
          Skip,
          Limit: this.limit,
          SortingDirection
        }).catch(this.error("Failed to get timeline: "))

        const photoCountPerYear = await this._getPhotoCountPerMonth({ Year: Number(yearId) }).catch(this.error("Failed to get photoCountPerYear: "))

        return {
          title: timeline.GroupedPhotos[0].Key,
          totalItemsCount: timeline.GroupedPhotos[0].Value.MatchedPhotoCount,
          items: timeline.GroupedPhotos[0].Value.Photos.map(mapPhoto),
          loading: false,
          monthsWithPhotos: photoCountPerYear.reverse()
        }
      }
    }
  }

  async getYears() {
    // Get photos without date
    const photosWithoutDate = await this._getPhotosWithoutDate({ Skip: 0, Limit: 1, SortingDirection: "Ascending" }).catch(
      this.error("Failed to photos without date: ")
    )

    const noDateImages = (photosWithoutDate.Photos || []).map((photo: any) => mapYears(photosWithoutDate, locale.get().generic.nodate, photo))

    //Get all years that contains photos and then get 1 photo for each year
    const photoCountPerYear = await this._getPhotoCountPerYear().then((response: any) => {
      const promises = response.map((year: any) =>
        this._getTimeline({
          Year: year.Year,
          Skip: 0,
          Limit: 1,
          SortingDirection: "Ascending"
        })
      )
      return Promise.all(promises)
    })

    const imageList = photoCountPerYear.flatMap((res: any) =>
      res.GroupedPhotos[0].Value.Photos.map((photo: any) => mapYears(res.GroupedPhotos[0].Value, res.GroupedPhotos[0].Key, photo))
    )

    return {
      items: [...imageList, ...noDateImages].sort((a: any, b: any) => b.title - a.title),
      loading: false
    }
  }

  async getSimilarPhotos(id: string) {
    const similars = await HttpClient.get(this.queryApiUrl + `/similarphotos/` + id, this.token).catch(this.error("Failed to get similars photos: "))

    return {
      totalItemsCount: similars.MatchedPhotoCount,
      items: (similars.Photos || []).map(mapPhoto),
      loading: false
    }
  }

  _getPhotoCountPerYear() {
    return HttpClient.get(this.queryApiUrl + `/photocountbyyear`, this.token)
  }

  _getPhotosWithoutDate(params: Params) {
    return HttpClient.get(
      this.queryApiUrl + `/photoswithoutphotodate/?Skip=${params.Skip}&Limit=${params.Limit}&SortingDirection=${params.SortingDirection}`,
      this.token
    )
  }

  _getPhotoCountPerMonth(params: Params) {
    return HttpClient.get(this.queryApiUrl + `/photocountbymonth/?Year=${params.Year}`, this.token)
  }

  _getTimeline(params: Params) {
    if (params.Month === 0 || !params.Month) {
      return HttpClient.get(
        this.queryApiUrl +
          `/timelinepaged/?year=${params.Year}&limit=${params.Limit}&sortingDirection=${params.SortingDirection}&skip=${params.Skip}`,
        this.token
      )
    } else {
      return HttpClient.get(
        this.queryApiUrl +
          `/timelinepaged/?year=${params.Year}&month=${params.Month}&limit=${params.Limit}&sortingDirection=${params.SortingDirection}&skip=${params.Skip}`,
        this.token
      )
    }
  }

  _getPhotos(params: Params) {
    return HttpClient.get(
      this.queryApiUrl +
        `/photos/?Skip=${params.Skip}&Limit=${params.Limit}&SortingField=${params.SortingField}&SortingDirection=${params.SortingDirection}`,
      this.token
    )
  }

  error(message: string) {
    return (ex: Error) => {
      console.error(message + JSON.stringify(ex))
      return Promise.resolve([])
    }
  }
}

export default Api
